import React, { RefObject } from 'react';
import { Recipe } from '../../../api/Recipe';
import { Box, Image, PlainButton } from '../../../components/ui';

interface EditRecipePhotoStageProps {
  recipe: Recipe;
  recipeImageSrc: string;
  isStickyRecipeHeaderVisible: boolean;
  stickyHeaderProgress: number;
  isSharedPhotoVisible: boolean;
  sharedPhotoStyle: React.CSSProperties | null;
  isImageOverlayOpen: boolean;
  imageWrapperRef: RefObject<HTMLDivElement | null>;
  tapedPhotoEffectRef: RefObject<HTMLDivElement | null>;
  stickyImageButtonRef: RefObject<HTMLButtonElement | null>;
  onOpenImageOverlay: () => void;
  onCloseImageOverlay: () => void;
}

const EditRecipePhotoStage: React.FC<EditRecipePhotoStageProps> = ({
  recipe,
  recipeImageSrc,
  isStickyRecipeHeaderVisible,
  stickyHeaderProgress,
  isSharedPhotoVisible,
  sharedPhotoStyle,
  isImageOverlayOpen,
  imageWrapperRef,
  tapedPhotoEffectRef,
  stickyImageButtonRef,
  onOpenImageOverlay,
  onCloseImageOverlay,
}) => (
  <>
    <Box
      ref={imageWrapperRef}
      className={`image-and-background-wrapper no-print ${isStickyRecipeHeaderVisible ? 'is-sticky-condensed' : ''}`}
      data-testid="edit-recipe-image-wrapper"
    >
      <Box className="checkered-tablecloth-bg" />
      <Box
        className="taped-photo-effect"
        ref={tapedPhotoEffectRef}
        data-testid="edit-recipe-photo-frame"
        style={{
          ...(isSharedPhotoVisible ? { visibility: 'hidden' } : undefined),
          ['--photo-progress' as string]: stickyHeaderProgress,
        }}
      >
        <PlainButton
          className="edit-recipe-image-button"
          onClick={onOpenImageOverlay}
          aria-label="Voir la photo en plein ecran"
        >
          <Image src={recipeImageSrc} fluid alt={recipe.title} />
        </PlainButton>
      </Box>
    </Box>

    <Box
      className={`edit-recipe-sticky-header no-print ${isStickyRecipeHeaderVisible ? 'is-visible' : ''}`}
      data-testid="edit-recipe-sticky-header"
      aria-hidden={!isStickyRecipeHeaderVisible}
      style={{ ['--sticky-progress' as string]: stickyHeaderProgress }}
    >
      <Box container="fluid" className="edit-recipe-sticky-header-inner app-desktop-shell">
        <PlainButton
          className="edit-recipe-sticky-image-button"
          onClick={onOpenImageOverlay}
          aria-label={isStickyRecipeHeaderVisible ? 'Voir la photo en plein ecran' : undefined}
          aria-hidden={!isStickyRecipeHeaderVisible}
          tabIndex={isStickyRecipeHeaderVisible ? 0 : -1}
          ref={stickyImageButtonRef}
          style={isSharedPhotoVisible ? { visibility: 'hidden' } : undefined}
        >
          <Image
            src={recipeImageSrc}
            className="edit-recipe-sticky-image"
            alt={recipe.title}
          />
        </PlainButton>
        {isStickyRecipeHeaderVisible && (
          <Box className="edit-recipe-sticky-title">{recipe.title}</Box>
        )}
      </Box>
    </Box>

    {sharedPhotoStyle && (
      <Box
        className="edit-recipe-shared-photo"
        style={sharedPhotoStyle}
        data-testid="edit-recipe-shared-image"
        aria-hidden={!isSharedPhotoVisible}
      >
        <Box
          className="taped-photo-effect edit-recipe-shared-photo-frame"
          data-testid="edit-recipe-shared-photo-frame"
          style={{
            transform: `rotate(${-3 + 3 * stickyHeaderProgress}deg)`,
            ['--photo-progress' as string]: stickyHeaderProgress,
          }}
        >
          <PlainButton
            className="edit-recipe-image-button edit-recipe-shared-photo-button"
            onClick={onOpenImageOverlay}
            aria-label="Voir la photo en plein ecran"
          >
            <Image
              src={recipeImageSrc}
              fluid
              alt={recipe.title}
            />
          </PlainButton>
        </Box>
      </Box>
    )}

    {isImageOverlayOpen && (
      <Box
        className="edit-recipe-image-overlay"
        data-testid="edit-recipe-image-overlay"
        role="dialog"
        aria-modal="true"
        aria-label={`Photo de ${recipe.title}`}
      >
        <PlainButton
          className="edit-recipe-image-backdrop"
          aria-label="Fermer la photo en cliquant hors image"
          onClick={onCloseImageOverlay}
        />
        <Box className="edit-recipe-image-frame">
          <PlainButton
            className="edit-recipe-image-close-button"
            aria-label="Fermer la photo"
            onClick={onCloseImageOverlay}
          >
            x
          </PlainButton>
          <Image
            src={recipeImageSrc}
            className="edit-recipe-image-fullscreen"
            alt={recipe.title}
          />
        </Box>
      </Box>
    )}
  </>
);

export default EditRecipePhotoStage;
